import { Link } from 'react-router-dom';
import { label, projectDate } from '@entities/ksp/format';
import type { KspWorkspace, ScheduleVersion } from '@entities/ksp/types';
import { Button } from '@shared/ui/Button';
import { Card, Grid, Muted, Nav, NavItem, Notice, Row, Small } from '../styles';
import { TABS } from '../types';
import { CollectionLoad } from './CollectionTable';

export interface WorkspaceHeaderProps {
  workspace: KspWorkspace;
  timezone: string;
  version?: ScheduleVersion;
  isFetching: boolean;
  canWrite: boolean;
  tab: string;
  params: URLSearchParams;
  message?: string;
  errors: string[];
  error?: unknown;
  onRefetch: () => void;
  onExport: () => void;
}

export function WorkspaceHeader({
  workspace: d,
  timezone,
  version,
  isFetching,
  canWrite,
  tab,
  params,
  message,
  errors,
  error,
  onRefetch,
  onExport,
}: WorkspaceHeaderProps) {
  return (
    <>
      <Row>
        <Link to="/agents/ksp">← Проекты</Link>
        <h1>{d.project_name}</h1>
        <Button $variant="ghost" onClick={onRefetch} disabled={isFetching}>
          Обновить
        </Button>
        {d.summary && (
          <Button $variant="secondary" disabled={d.history_read_only} onClick={onExport}>
            Выгрузить
          </Button>
        )}
      </Row>
      <Muted>
        {timezone} ·{' '}
        {version
          ? `${label(version.kind)}, ревизия ${version.revision}, ${label(version.approval)}`
          : 'Версия графика не выбрана'}
        {isFetching ? ' · обновление данных' : ''}
      </Muted>
      {!canWrite && (
        <Notice>
          Доступ для просмотра. Операции изменения доступны назначенным специалистам.
        </Notice>
      )}
      {d.history_read_only && (
        <Notice>
          Исторический срез: настройки, исходные данные и расчёт сохранены в выбранной версии.
          Изменения доступны после возврата к текущим данным.
        </Notice>
      )}
      {d.historical_snapshot_missing && (
        <Notice>
          У выбранной версии нет сохранённого расчётного снимка. Показаны утверждённые исходные
          данные; исторический факт, прогноз, KPI и оперативные предупреждения недоступны.
        </Notice>
      )}
      {error && (
        <Notice $error>
          Обновление источника недоступно; показан последний полученный срез.
        </Notice>
      )}
      <Nav>
        {TABS.map(([id, title]) => (
          <NavItem
            key={id}
            $active={tab === id}
            to={`?${new URLSearchParams({ ...Object.fromEntries(params), tab: id })}`}
          >
            {title}
          </NavItem>
        ))}
      </Nav>
      {Object.entries(d.collections ?? {}).some(([, meta]) => meta.truncated) && (
        <Card>
          <h3>Дополнительные страницы данных</h3>
          <Grid>
            {Object.entries(d.collections ?? {})
              .filter(([, meta]) => meta.truncated)
              .map(([key]) => (
                <CollectionLoad key={key} collection={key} />
              ))}
          </Grid>
        </Card>
      )}
      {message && <Notice>{message}</Notice>}
      {d.operational && (
        <Small>
          Оперативный срез {projectDate(d.operational.operational_as_of, timezone)} · ревизия{' '}
          {d.operational.revision} · открытых предупреждений{' '}
          {d.operational.open_milestone_alert_count}. Численные показатели относятся к
          сохранённому расчётному снимку.
        </Small>
      )}
      {errors.length > 0 && (
        <Card>
          <h3>Журнал ошибок действий</h3>
          {errors.map((issue, i) => (
            <Notice $error key={i}>
              {issue}
            </Notice>
          ))}
        </Card>
      )}
      {d.validation_issues?.length ? (
        <Card>
          <h3>Проверка исходных данных</h3>
          {d.validation_issues.map((issue, i) => (
            <Notice $error key={i}>
              {issue.code} · {issue.sheet} {issue.cell} · {issue.message ?? issue.reason}
            </Notice>
          ))}
        </Card>
      ) : null}
    </>
  );
}
